import { Logger } from "@moment/logging";

import { env } from "../env";
import { writeTextFile } from "../filesystem/filesystem.internal";
import { getDocumentDotMomentPath } from "../paths/paths.internal";
import type { QueryError, QueryResponse, QueryTiming } from "../request-page/request-page.types";

const logger = new Logger("diagnostics-snapshot");

// =============================================================================
// Types
// =============================================================================

interface CellEntry {
	cellId: string;
	cellName?: string;
	status: string;
	content: string;
	timestamp: string;
}

interface RequestEntry {
	pageName?: string;
	status: string;
	kind?: string;
	statusLine?: string;
	duration?: string;
	content: string;
	timestamp: string;
}

interface PageSnapshot {
	documentId: string;
	pageId: string;
	cells: Map<string, CellEntry>;
	requestResult?: RequestEntry;
}

// =============================================================================
// In-memory cache and write queue
// =============================================================================

/** Cache of page snapshots keyed by "documentId:pageId" */
const snapshotCache = new Map<string, PageSnapshot>();

/** Serialized write queue per page to prevent concurrent writes */
const writeQueue = new Map<string, Promise<void>>();

/** Tracks which directories have been created to avoid redundant mkdir calls */
const createdDirs = new Set<string>();

// =============================================================================
// Serialization helpers
// =============================================================================

function serializeValue(value: unknown): string {
	if (value === undefined) {
		return "undefined";
	}
	if (value === null) {
		return "null";
	}

	// React elements — these are JSX objects not yet mounted to the DOM.
	// We cannot serialize them to HTML without a full React render pass.
	// The hook defers writes for DOM nodes (which ARE mounted), but raw
	// React elements passed as cell values will show this placeholder.
	if (typeof value === "object" && value !== null && "$$typeof" in value) {
		return "[React Element — not yet mounted to DOM; check the rendered page for visual output]";
	}

	// Functions
	if (typeof value === "function") {
		return `[Function: ${value.name || "anonymous"}]`;
	}

	// DOM nodes — serialize the full nested HTML
	try {
		if (typeof Node !== "undefined" && value instanceof Node) {
			if ("outerHTML" in value) {
				return (value as Element).outerHTML;
			}
			// Text nodes, comments, etc.
			return value.textContent ?? `[DOM Node: ${value.nodeName}]`;
		}
	} catch {
		// Node not available in non-browser environments
	}

	// Error objects
	if (value instanceof Error) {
		const lines = [`${value.name}: ${value.message}`];
		if (value.stack) {
			// Skip first line of stack — V8 already includes "Name: message" there
			lines.push(
				...value.stack
					.split("\n")
					.slice(1, 11)
					.map((l) => `  ${l.trim()}`)
			);
		}
		return lines.join("\n");
	}

	// Primitives
	if (typeof value !== "object") {
		return String(value);
	}

	// Objects and arrays
	try {
		return JSON.stringify(value, null, 2);
	} catch {
		return String(value);
	}
}

function getTimestamp(): string {
	return new Date().toISOString();
}

// =============================================================================
// File I/O
// =============================================================================

async function getSnapshotDirPath(documentId: string, pageId: string): Promise<string> {
	const { join } = await import("@tauri-apps/api/path");
	const dotMomentPath = await getDocumentDotMomentPath(documentId);
	return join(dotMomentPath, "diagnostics", pageId);
}

async function getSnapshotFilePath(documentId: string, pageId: string): Promise<string> {
	const { join } = await import("@tauri-apps/api/path");
	const dirPath = await getSnapshotDirPath(documentId, pageId);
	return join(dirPath, "snapshot.log");
}

/** Normalize path separators to forward slashes for cross-platform cache keys. */
function normalizeDirPath(p: string): string {
	return p.replace(/\\/g, "/");
}

async function ensureDir(dirPath: string): Promise<void> {
	const normalizedPath = normalizeDirPath(dirPath);
	if (createdDirs.has(normalizedPath)) {
		return;
	}

	const { mkdir, exists: fsExists } = await import("@tauri-apps/plugin-fs");
	try {
		if (!(await fsExists(dirPath))) {
			await mkdir(dirPath, { recursive: true });
		}
		createdDirs.add(normalizedPath);
	} catch {
		// Directory may already exist — only cache if it actually does now
		if (await fsExists(dirPath).catch(() => false)) {
			createdDirs.add(normalizedPath);
		}
	}
}

function serializeSnapshot(snapshot: PageSnapshot): string {
	const lines: string[] = [
		`# Page Diagnostics Snapshot`,
		`# Document: ${snapshot.documentId}`,
		`# Page: ${snapshot.pageId}`,
		`# Updated: ${getTimestamp()}`,
		"",
	];

	// Code cells
	for (const [, cell] of snapshot.cells) {
		const nameLabel = cell.cellName ? `${cell.cellName} ` : "";
		lines.push(`## Code Cell: ${nameLabel}(cell=${cell.cellId})`);
		lines.push(`Timestamp: ${cell.timestamp}`);
		lines.push(`Status: ${cell.status}`);
		lines.push("---");
		lines.push(cell.content);
		lines.push("");
	}

	// Request result
	if (snapshot.requestResult) {
		const req = snapshot.requestResult;
		const nameLabel = req.pageName ? `${req.pageName}` : "unnamed";
		lines.push(`## Request Page: ${nameLabel}`);
		lines.push(`Timestamp: ${req.timestamp}`);
		lines.push(`Status: ${req.status}`);
		if (req.kind) {
			lines.push(`Kind: ${req.kind}`);
		}
		if (req.statusLine) {
			lines.push(req.statusLine);
		}
		if (req.duration) {
			lines.push(`Duration: ${req.duration}`);
		}
		lines.push("---");
		lines.push(req.content);
		lines.push("");
	}

	return lines.join("\n");
}

function getOrCreateSnapshot(documentId: string, pageId: string): PageSnapshot {
	const key = `${documentId}:${pageId}`;
	let snapshot = snapshotCache.get(key);
	if (!snapshot) {
		snapshot = { documentId, pageId, cells: new Map(), requestResult: undefined };
		snapshotCache.set(key, snapshot);
	}
	return snapshot;
}

async function persistSnapshot(documentId: string, pageId: string): Promise<void> {
	const key = `${documentId}:${pageId}`;
	const snapshot = snapshotCache.get(key);
	if (!snapshot) {
		return;
	}

	const dirPath = await getSnapshotDirPath(documentId, pageId);
	await ensureDir(dirPath);

	const filePath = await getSnapshotFilePath(documentId, pageId);
	const content = serializeSnapshot(snapshot);
	await writeTextFile(filePath, content);
}

function enqueueWrite(documentId: string, pageId: string): void {
	const key = `${documentId}:${pageId}`;
	const previous = writeQueue.get(key) ?? Promise.resolve();
	const next = previous
		.then(async () => {
			await persistSnapshot(documentId, pageId);
		})
		.catch((err) => {
			logger.error("Failed to write diagnostics snapshot", { documentId, pageId, err });
		});

	writeQueue.set(key, next);
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Write a code cell result snapshot.
 * Updates the cell's entry in the page's snapshot and overwrites the file.
 */
export function writeCodeCellSnapshot(params: {
	documentId: string;
	pageId?: string;
	cellId: string;
	cellName?: string;
	result: {
		type: string;
		value?: unknown;
		runtimeError?: Error;
		staticError?: Error;
		pending?: boolean;
	};
}): void {
	if (!env.isDesktopApp) {
		return;
	}
	if (!params.pageId) {
		return;
	}

	const { documentId, pageId, cellId, cellName, result } = params;
	const snapshot = getOrCreateSnapshot(documentId, pageId);

	let status: string;
	let content: string;

	// Static errors take priority — a cell can have type "FULFILLED" from a
	// previous run while the current code has a parse/compile error.
	if (result.staticError) {
		status = "ERROR (static)";
		content = serializeValue(result.staticError);
	} else if (result.type === "FULFILLED") {
		status = "FULFILLED";
		content = serializeValue(result.value);
	} else if (result.type === "REJECTED" && result.runtimeError) {
		status = "REJECTED";
		content = serializeValue(result.runtimeError);
	} else if (result.type === "PENDING") {
		status = "PENDING";
		content = result.pending ? "(executing...)" : "(queued)";
	} else {
		status = result.type;
		content = "(no output)";
	}

	snapshot.cells.set(cellId, {
		cellId,
		cellName,
		status,
		content,
		timestamp: getTimestamp(),
	});

	enqueueWrite(documentId, pageId);
}

/**
 * Write a request page result snapshot.
 * Updates the request entry in the page's snapshot and overwrites the file.
 */
export function writeRequestPageSnapshot(params: {
	documentId: string;
	pageId?: string;
	pageName?: string;
	data?: QueryResponse;
	error?: QueryError;
	timing?: QueryTiming;
}): void {
	if (!env.isDesktopApp) {
		return;
	}
	if (!params.pageId) {
		return;
	}

	const { documentId, pageId, pageName, data, error, timing } = params;
	const snapshot = getOrCreateSnapshot(documentId, pageId);

	let status: string;
	let kind: string | undefined;
	let statusLine: string | undefined;
	let duration: string | undefined;
	let content: string;

	if (timing?.durationMs !== undefined) {
		duration = `${timing.durationMs}ms`;
	}

	if (data) {
		kind = data.kind;

		switch (data.kind) {
			case "http": {
				status = data.ok ? "success" : "error";
				statusLine = `HTTP ${data.status} ${data.statusText}`;
				content = serializeValue(data.parsedBody);
				break;
			}
			case "postgres": {
				status = "success";
				duration = `${data.durationMs}ms`;
				const header = `Command: ${data.command}\nColumns: ${data.columns.map((c) => `${c.name} (${c.dataType})`).join(", ")}\nRows: ${data.rowCount}`;
				content = `${header}\n\n${serializeValue(data.rows)}`;
				break;
			}
			case "javascript": {
				status = "success";
				duration = `${data.durationMs}ms`;
				content = serializeValue(data.parsedBody);
				break;
			}
			default: {
				status = "success";
				content = serializeValue(data);
			}
		}
	} else if (error) {
		status = "error";
		content = `[${error.code}] ${error.message}`;
		if (error.responseBody) {
			content += `\n\nResponse body:\n${error.responseBody}`;
		}
	} else {
		status = "unknown";
		content = "(no data)";
	}

	// If we have both data and error (e.g., HTTP 4xx with a classified error),
	// append the error code and message so agents see the actionable details.
	if (data && error) {
		status = "error";
		content += `\n\nError: [${error.code}] ${error.message}`;
		if (error.responseBody) {
			content += `\n\nResponse body:\n${error.responseBody}`;
		}
	}

	snapshot.requestResult = {
		pageName,
		status,
		kind,
		statusLine,
		duration,
		content,
		timestamp: getTimestamp(),
	};

	enqueueWrite(documentId, pageId);
}

/**
 * Remove a single cell's entry from the page snapshot and persist the update.
 * Call this when a code cell is deleted so the snapshot no longer references it.
 */
export function removeCellFromSnapshot(params: {
	documentId: string;
	pageId: string;
	cellId: string;
}): void {
	if (!env.isDesktopApp) {
		return;
	}
	const { documentId, pageId, cellId } = params;
	const key = `${documentId}:${pageId}`;
	const snapshot = snapshotCache.get(key);
	if (!snapshot) {
		return;
	}
	if (!snapshot.cells.has(cellId)) {
		return;
	}
	snapshot.cells.delete(cellId);
	enqueueWrite(documentId, pageId);
}

/**
 * Clear all cached snapshots and write queues for a document.
 * Call this when a document is unloaded to prevent memory leaks.
 */
export function clearDocumentSnapshots(documentId: string): void {
	for (const key of snapshotCache.keys()) {
		if (key.startsWith(`${documentId}:`)) {
			snapshotCache.delete(key);
		}
	}
	for (const key of writeQueue.keys()) {
		if (key.startsWith(`${documentId}:`)) {
			writeQueue.delete(key);
		}
	}
	// Use path-separator-aware check so a documentId that is a substring of
	// another path component doesn't cause false evictions.
	const segment = `/${documentId}/`;
	for (const dir of createdDirs) {
		if (dir.includes(segment)) {
			createdDirs.delete(dir);
		}
	}
}
